import _ from 'lodash';
import { unsafeHTML } from '~/lib/bn-lit-element';
import { LitEntity, SirenActionMixin, html, css } from '../../utility/lit-entity';
import { ActiveWorkOrderWatcherMixin } from '../../utility/active-work-order-watcher-mixin';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-action';
import '../../common/bn-flash';
import '../../common/bn-loading';
import './bin-issue-state-confirm-not-work-order';
import './bin-issue-state-physical-issue-question';
import './bin-issue-state-physical-issue-form';
import './bin-issue-state-level-question';
import './bin-issue-state-level-select';
import './bin-issue-state-level-form-height';
import './bin-issue-state-level-form-weight';
import './bin-issue-state-dimensions-question';
import './bin-issue-state-dimensions-form';
import './bin-issue-state-comments-form';
import './bin-issue-state-thank-you';
import { STATES, START_STATE, STATE_TRANSITIONS, TRANSITION_NAMES, MILL_BIN_START_STATE, WARN_START_STATE } from './bin-issue-reporter-meta';

class BinIssueReporter extends ActiveWorkOrderWatcherMixin(SirenActionMixin(LitEntity)) {
  constructor() {
    super();
    this.stateHistory = [];

    this.collectedData = {};
    this.correctedLevels = {};
    this.correctedDimensions = {};
    this.isFetchingActiveWorkOrder = true;
  }

  static get properties() {
    return {
      state: { type: String, attribute: false },
      correctedLevels: { type: Object, attribute: false },
      correctedDimensions: { type: Object, attribute: false },
      collectedData: { type: Object, attribute: false },
      comments: { type: String, attribute: false },
      physicalIssue: { type: Boolean, attribute: false },
      issuesHref: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
      * {
        width: 100%;
      }
    `];
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('transition', this._onTransition);
    this.addEventListener('clear-fields', this._onClearFields);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('transition', this._onTransition);
    this.removeEventListener('clear-fields', this._onClearFields);
  }

  render() {
    let content;
    if (!this.issuesEntity || this.isFetchingActiveWorkOrder) {
      content = html`<bn-loading></bn-loading>`;
    } else {
      const tagName = `bin-issue-state-${STATES[this.state].tagPostFix}`;
      content = unsafeHTML(`
        <${tagName}
          href="${this.href}"
          ${this.stateHistory.length === 0 ? 'no-back-button' : ''}
          data="${_.escape(JSON.stringify(this.collectedData))}"
        ></${tagName}>
      `);
    }

    return html`
      <lit-entity .href="${this.issuesHref}" @update="${this._onIssueUpdate}"></lit-entity>
      <bn-flash id="flash"></bn-flash>
      ${content}
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.issuesHref = this.getHref('https://api.binsentry.com/rel/user-reported-issues');

    // not going to wait for active work orders since there will be none
    if (!this.workOrderHrefs.length) {
      this.isFetchingActiveWorkOrder = false;
    }
  }

  _onTransition(e) {
    const { transitionName, collectedData } = e.detail;
    Object.assign(this.collectedData, collectedData || {});
    this._transition(transitionName);
  }

  _onClearFields(e) {
    const { fields } = e.detail;
    fields.forEach((field) => delete this.collectedData[field]);
  }

  _transition(transitionName) {
    if (transitionName === TRANSITION_NAMES.back) {
      this.state = this.stateHistory.pop();
      return;
    }

    if (transitionName === TRANSITION_NAMES.submit) {
      this._submitAction();
    }

    if (transitionName === TRANSITION_NAMES.cancel) {
      this.cancel();
      return;
    }

    const nextState = STATE_TRANSITIONS[this.state][transitionName];
    if (!nextState) {
      // eslint-disable-next-line no-console
      console.error(`Invalid state transition, in state: ${this.state} and trying to transition: ${transitionName}`);
      return;
    }

    // avoid pushing the warning state to the stack
    if (this.state !== WARN_START_STATE) {
      this.stateHistory.push(this.state);
    }
    this.state = nextState;
  }

  async _submitAction() {
    const action = this.issuesEntity.getAction('create-user-reported-issue');

    try {
      this.loading = true;
      const fields = this.getActionFields(action);

      const dimensions = this.collectedData.correctedDimensions;
      if (dimensions && action.getField('correctedDimensions[topHeight]')) {
        this._present(dimensions.topHeight) && fields.set('correctedDimensions[topHeight]', dimensions.topHeight);
        this._present(dimensions.bodyHeight) && fields.set('correctedDimensions[bodyHeight]', dimensions.bodyHeight);
        this._present(dimensions.hopperHeight) && fields.set('correctedDimensions[hopperHeight]', dimensions.hopperHeight);
        this._present(dimensions.ringCount) && fields.set('correctedDimensions[ringCount]', dimensions.ringCount);
        this._present(dimensions.diameter) && fields.set('correctedDimensions[binRadius]', dimensions.diameter / 2);
      }
      if (this.collectedData.correctedLevelDate && action.getField('disputedLevelId')) {
        fields.set('disputedLevelId', this.collectedData.disputedLevelId);
        fields.set('correctedLevel[dateTime]', this.collectedData.correctedLevelDate);
        fields.set('correctedLevel[feedHeight]', this.collectedData.correctedLevelHeight);
        fields.set('correctedLevel[weight]', this.collectedData.correctedLevelWeight);
      }
      this.collectedData.comments && fields.set('comments', this.collectedData.comments);
      if (action.getField('physicalIssue')) {
        fields.set('physicalIssue', !!this.collectedData.physicalIssue);
      }

      const { error } = await this.submitAction(action, fields);
      if (error) {
        this._onError(error);
        this.loading = false;
      }
    } catch (error) {
      this._onError(error);
      this.loading = false;
    }
  }

  _present(value) {
    return value !== undefined;
  }

  _onError(error) {
    const { message } = error;
    const flash = this.shadowRoot.querySelector('#flash');
    flash.clear();
    flash.critical(html`
      Failed To Submit Issue
      <br/>
      <code>${message}</code>
    `);
  }

  _onIssueUpdate(e) {
    const { entity } = e.detail;
    if (!entity) {
      return;
    }

    this.issuesEntity = entity;
    const action = this.issuesEntity.getAction('create-user-reported-issue');

    if (action.getField('physicalIssue')) {
      this.state = START_STATE;
    } else {
      this.state = MILL_BIN_START_STATE;
    }

    this.requestUpdate();
  }

  // this method isn't called if workOrderHrefs.length is 0
  _onWorkOrdersUpdate(e) {
    super._onWorkOrdersUpdate(e);
    if (this.activeWorkOrder) {
      this.state = WARN_START_STATE;
    }
    this.isFetchingActiveWorkOrder = false;
  }

  cancel() {
    this.dispatchEvent(new CustomEvent('cancel'));
  }
}

window.customElements.define('bin-issue-reporter', BinIssueReporter);
